/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * Contains functions used as resolve functions for ui-router states.
 */

import { firstValueFrom, take } from 'rxjs';
import { find } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import * as SystemInfoActions from 'ng/root-store/system-info/system-info.actions';
import * as SystemInfoSelectors from 'ng/root-store/system-info/system-info.selectors';
import { STORE_TOKEN } from 'ng/root-store/root-store.tokens';
import { DevLoggerService } from 'ng/modules/core/services/dev-logger.service';
import { Auth } from 'ajs/modules/core/services/auth/auth.service';
import { SystemConfig } from 'ajs/modules/system/factories/system-config.item.factory';
import { ReportProfileItem } from 'ng/modules/items/factories/report-profile.item.factory';
import { GSLB } from 'items/gslb.item.factory';

export const loadSystemInfo = {
    token: 'loadSystemInfo',
    resolveFn: (store: Store) => {
        store.dispatch(SystemInfoActions.loadSystemInfo());

        return firstValueFrom(
            store.select(SystemInfoSelectors.selectLoaded).pipe(find(loaded => Boolean(loaded))),
        );
    },
    deps: [STORE_TOKEN],
};

// Type of loadSystemInfo object. To be used as type in resolver function of resolveGslb.
type TLoadSystemInfo = typeof loadSystemInfo;

export const loadedSystemConfigService = {
    token: 'loadedSystemConfigService',
    resolveFn: (
        auth: Auth,
        systemConfigService: SystemConfig,
        devLoggerService: DevLoggerService,
    ) => {
        const isSystemConfigAllowed = auth.isAllowed('systemconfiguration', 'w');

        if (isSystemConfigAllowed) {
            return systemConfigService.load()
                .then(() => systemConfigService)
                .catch((error: any) => devLoggerService.error(error));
        }
    },
    deps: ['Auth', 'systemConfigService', 'devLoggerService'],
};

/**
 * Load Report Profile instance.
 */
export const resolveReportProfile = {
    token: 'resolveReportProfile',
    resolveFn: (
        loadItem: any,
        ReportProfileItem: ReportProfileItem,
    ) => {
        return loadItem({ id: 'default' }, ReportProfileItem);
    },
    deps: ['loadItem', ReportProfileItem],
};

/**
 * Load GSLB instance if appropriate permissions are set. Otherwise,
 * return null and let child route handle it.
 */
export const resolveGslb = {
    token: 'resolveGslb',
    resolveFn: (
        Auth: Auth,
        loadItem: any,
        GSLB: GSLB,
        store: Store,
        loadSystemInfo: TLoadSystemInfo,
    ) => {
        let gslbId;

        store.select(SystemInfoSelectors.selectGslbId)
            .pipe(take(1))
            .subscribe(id => gslbId = id);

        if (Auth.isAllowed('gslb') && gslbId) {
            return loadItem(gslbId, GSLB);
        } else {
            return null;
        }
    },
    deps: ['Auth', 'loadItem', GSLB, STORE_TOKEN, 'loadSystemInfo'],
};
